import type { ColTypeDef } from 'ag-grid-community';
import { useServerSideNumberColumnType } from 'components/shared/Blotter/Grid/Hooks/useNumberColumnType';
import { useServerSidePercentColumnType } from 'components/shared/Blotter/Grid/Hooks/usePercentColumnType';
import { useServerSideStringColumnType } from 'components/shared/Blotter/Grid/Hooks/useStringColumnType';
import { useMemo } from 'react';

import { useServerSideBooleanColumnType } from './useBooleanColumnType';
import { useServerSideCurrencyColumnType } from './useCurrencyColumnType';
import { useServerSideDateColumnType } from './useDateColumnType';
import { useServerSideDateOrTimeColumnType } from './useDateOrTimeColumnType';
import { useServerSideDateTimeColumnType } from './useDateTimeColumnType';
import { useServerSideUnderlyingColumnType } from './useUnderlyingColumnType';

type ColumnDefinitions<T> = Record<string, ColTypeDef<T>>;

function keepOrRemoveFilter<T>(columnDefinition: ColTypeDef<T>, keepFilter: boolean) {
  if (keepFilter) {
    return columnDefinition;
  } else {
    return { ...columnDefinition, filter: false };
  }
}

export function useWithExplicitFilering<T>(
  columnDefinitions: ColumnDefinitions<T>,
  filteredColumnTypes: string[] | undefined,
) {
  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(columnDefinitions).map(([colType, colDef]) => [
          colType,
          keepOrRemoveFilter(colDef, filteredColumnTypes?.includes(colType) ?? false),
        ]),
      ),
    [columnDefinitions, filteredColumnTypes],
  );
}

export function useServerSideDefaultColumnTypes<T>(
  customColumns: ColumnDefinitions<T>,
  precision = 4,
): ColumnDefinitions<T> {
  const booleanColumn = useServerSideBooleanColumnType<T>();
  const dateColumn = useServerSideDateColumnType<T>();
  const dateTimeColumn = useServerSideDateTimeColumnType<T>();
  const dateOrTimeColumn = useServerSideDateOrTimeColumnType<T>();
  const stringColumn = useServerSideStringColumnType<T>();
  const underlyingColumn = useServerSideUnderlyingColumnType<T>();
  const currencyColumn = useServerSideCurrencyColumnType<T>();
  const numberColumn = useServerSideNumberColumnType<T>(precision);
  const percentColumn = useServerSidePercentColumnType<T>(precision);

  return useMemo<Record<string, ColTypeDef<T>>>(
    () => ({
      boolean: booleanColumn,
      date: dateColumn,
      datetime: dateTimeColumn,
      dateOrTime: dateOrTimeColumn,
      string: stringColumn,
      underlyings: underlyingColumn,
      currency: currencyColumn,
      number: numberColumn,
      percent: percentColumn,
      ...customColumns,
    }),
    [
      booleanColumn,
      currencyColumn,
      customColumns,
      dateColumn,
      dateOrTimeColumn,
      dateTimeColumn,
      numberColumn,
      percentColumn,
      stringColumn,
      underlyingColumn,
    ],
  );
}
